import { Injectable } from '@angular/core'
import { DateService } from '@se-po/shared-data-access-services'
import { DateTime } from 'luxon'
import { PersonaEligibilityCredential } from 'se-resource-types/dist/lib/EligibilitySearchService'
import { PersonaEligibility } from '../models'
import { CredentialCatalog } from '../models/credential-catalog'
import { CredentialListCategory, CredentialListHistory, CredentialListItem, CredentialListUsage } from '../models/credential-list-category'
import { EligibilityCredential } from '../models/eligibility-credential'
import { EligibilityRule } from '../models/eligibility-rule'

@Injectable({
  providedIn: 'root'
})
export class CredentialListService {
  public static INACTIVE_CREDENTIAL_LABEL = 'Inactive Credentials'

  constructor(private dateService: DateService) {
    // noop
  }

  public static getDefaultCategories(): CredentialListCategory[] {
    return [{
      label: 'Safety',
      items: [],
      order: 1
    }, {
      label: 'Training & Education',
      items: [],
      order: 2,
    }, {
      label: this.INACTIVE_CREDENTIAL_LABEL,
      items: [],
      order: 3,
      collapsible: true,
      collapsed: true
    }]
  }

  public static compareCredentials(
    credentialA: EligibilityCredential | PersonaEligibilityCredential,
    credentialB: EligibilityCredential | PersonaEligibilityCredential
  ): boolean {
    return credentialA.credential_system === credentialB.credential_system
      && credentialA.credential_type === credentialB.credential_type
      && credentialA.credential_value === credentialB.credential_value
  }

  public static compareCatalogToCredential(
    catalog: CredentialCatalog,
    credential: EligibilityCredential
  ): boolean {
    return catalog.rule_system === credential.rule_system
      && (!catalog.rule_type || catalog.rule_type === credential.rule_type)
      && (!catalog.rule_value || catalog.rule_value === credential.rule_value)
  }

  public static compareRuleToCredential(
    rule: EligibilityRule,
    credential: EligibilityCredential
  ): boolean {
    return rule.originator_system === credential.rule_system
      && rule.originator_type === credential.rule_type
      && rule.originator_id === credential.rule_value
  }

  public static getUsages(credential: EligibilityCredential, personaEligibilities: PersonaEligibility[]): CredentialListUsage[] {
    return personaEligibilities.map(pe => {
      const matchedCredential = pe.eligibility_credentials.find(ec => CredentialListService.compareCredentials(ec, credential))
      const matchedRule = pe.eligibility_rule_set.eligibility_rules.find(
        er => CredentialListService.compareRuleToCredential(er, credential))
      const organization = matchedRule && matchedRule.linked_eligibility_rule_set
        ? matchedRule.linked_eligibility_rule_set.boss_organization
        : pe.eligibility_rule_set.boss_organization
      if (matchedCredential) {
        return {
          organization: organization.name,
          status: matchedCredential.status,
          eligibility_status: matchedCredential.eligibility_status,
          expires_at: matchedCredential.expires_at,
          timezone: organization.timezone
        } as CredentialListUsage
      }
    })
      .filter(item => !!item)
      .sort((a, b) => (a.expires_at ? DateTime.fromISO(a.expires_at).toMillis() : 0) -
          (b.expires_at ? DateTime.fromISO(b.expires_at).toMillis() : 0))
      .reverse()
  }

  public static getCourseName(credential: EligibilityCredential): string {
    const credentialData = credential.parseCredentialData() || {}
    if (credential.rule_type === 'ngin_credential') return credentialData.label || credential.credential_label
    switch (credentialData.course || credentialData.code || undefined) {
      case 'safesport':
      case 'core':
        return 'Core'
      case 'refresher1':
        return 'Refresher 1'
      case 'refresher2':
        return 'Refresher 2'
      case 'refresher3':
        return 'Refresher 3'
      default:
        return credential.credential_label
    }
  }

  public getCredentialDescription(completed: string, timezone?: string): string {
    if (completed) {
      return `Completed ${this.dateService.formatDateTime(completed, timezone)}`
    }
    return ''
  }

  public getExpirationDescription(expires: string, timezone?: string): string {
    if (expires) {
      if (DateTime.fromISO(expires).startOf('day') <= DateTime.now().startOf('day')) {
        return `Expired ${this.dateService.formatDateTime(expires, timezone)}`
      } else {
        return `Expires ${this.dateService.formatDateTime(expires, timezone)}`
      }
    }
  }

  public findCredentialListCategories(
    credentials: EligibilityCredential[],
    credentialCatalog: CredentialCatalog[],
    eligibilities: PersonaEligibility[],
    timezone: string
  ): CredentialListCategory[] {
    let categories = CredentialListService.getDefaultCategories()
    const catalog = this.hideLegacySafesportCatalog(credentials, credentialCatalog)
    const credentialGroups = this.groupCredentials(credentials)
    for (const [i, credentialGroup] of credentialGroups) {
      const credential = credentialGroup[0]
      const matchedCatalog = catalog.find(c => CredentialListService.compareCatalogToCredential(c, credential))
      if (matchedCatalog) {
        this.appendCredentialToCategory(credentialGroup, matchedCatalog, eligibilities, categories, timezone)
      }
    }
    categories = this.removeEmptyCategories(categories)
    return this.sortCredentialList(categories)
  }

  /**
   * Group similar credentials by rule triad. The most current credential renders
   * as a CredentialListItem, with historical records nested on the history array
   */
  protected groupCredentials(credentials: EligibilityCredential[]): Map<string, EligibilityCredential[]> {
    const credentialGroups = new Map<string, EligibilityCredential[]>()
    // Date completed desc
    const orderedCredentials = credentials.sort(
      (a, b) => (DateTime.fromISO(a.completed_at).toMillis() - DateTime.fromISO(b.completed_at).toMillis())).reverse()
    for (const credential of orderedCredentials) {
      const key = [credential.rule_system, credential.rule_type, credential.rule_value].join(':')
      if (!credentialGroups.has(key)) {
        credentialGroups.set(key, [])
      }
      credentialGroups.get(key).push(credential)
    }
    return credentialGroups
  }

  /**
   * Corelate a credential with persona eligibility documents
   * and the credential catalog to provide more context to the
   * user
   */
  protected appendCredentialToCategory(
    credentials: EligibilityCredential[],
    catalog: CredentialCatalog,
    personaEligibilities: PersonaEligibility[],
    categories: CredentialListCategory[],
    timezone: string
  ): void {
    let expirationDescription: string
    const credential = credentials[0]
    const usages = CredentialListService.getUsages(credential, personaEligibilities)
    let isPastCredential = false
    if (usages.length) {
      isPastCredential = usages.filter(u =>
        DateTime.fromISO(u.expires_at).startOf('day') <= DateTime.now().startOf('day')).length === usages.length
      expirationDescription = this.getExpirationDescription(usages[0].expires_at, usages[0].timezone)
    } else {
      isPastCredential = DateTime.fromISO(credential.expires_at).startOf('day') <= DateTime.now().startOf('day')
      expirationDescription = this.getExpirationDescription(credential.expires_at, timezone)
    }
    let categoryName = catalog.category
    if (isPastCredential) {
      categoryName = CredentialListService.INACTIVE_CREDENTIAL_LABEL
    }
    const category = categories.find(c => c.label === categoryName)

    const description = [this.getCredentialDescription(credential.completed_at, timezone)]
    if (expirationDescription) description.push(expirationDescription)

    category.items.push(
      {...this.getCredentialListItem(credential, catalog),
        history: this.getCredentialHistory(credentials, personaEligibilities, timezone),
        description
      }
    )
  }

  /**
   * Simplified view model for history records
   */
  protected getCredentialHistory(
    credentials: EligibilityCredential[],
    personaEligibilities: PersonaEligibility[],
    timezone: string
  ): CredentialListHistory[] {
    const history: CredentialListHistory[] = []
    for (const credential of credentials) {
      const usages = CredentialListService.getUsages(credential, personaEligibilities)
      const historyItem = {
        label: CredentialListService.getCourseName(credential),
        completed: credential.completed_at ? this.dateService.formatDateTime(credential.completed_at, timezone) : '',
        expires: credential.expires_at ? this.dateService.formatDateTime(credential.expires_at, timezone) : ''
      } as CredentialListHistory
      if (usages.length) {
        historyItem.expires = usages[0].expires_at ? this.dateService.formatDateTime(usages[0].expires_at, usages[0].timezone) : ''
      }
      history.push(historyItem)
    }
    return history
  }

  protected removeEmptyCategories(categories: CredentialListCategory[]): CredentialListCategory[] {
    return categories.filter(c => c.items.length > 0)
  }

  protected sortCredentialList(categories: CredentialListCategory[]): CredentialListCategory[] {
    categories = categories.sort((a, b) => Math.sign(a.order - b.order) || a.label.localeCompare(b.label))
    categories.forEach(c => c.items.sort((a, b) => Math.sign(a.order - b.order) || a.label.localeCompare(b.label)))
    return categories
  }

  /**
   * If the user has successfully migrated to absorb credentials,
   * remove 77 media credentials from the catalog
   */
   private hideLegacySafesportCatalog(credentials: EligibilityCredential[], credentialCatalog: CredentialCatalog[]): CredentialCatalog[] {
    const absorbCredential = credentials.find(c => c.rule_system === 'SeAbsorbService')
    if (absorbCredential) {
      return credentialCatalog.filter(c => !(c.rule_system === 'SeSafesportService'))
    }
    return credentialCatalog
  }

  private getCredentialListItem(credential: EligibilityCredential, catalog: CredentialCatalog): CredentialListItem {
    const credentialData = credential.parseCredentialData()
    return {
      credential: {
        system: credential.credential_system,
        type: credential.credential_type,
        value: credential.credential_value
      },
      order: catalog.order,
      logo: catalog.logo,
      organization: catalog.organization,
      label: credential.credential_label || credentialData.label,
      rule: {
        system: credential.rule_system,
        type: credential.rule_type,
        value: credential.rule_value
      }
    } as CredentialListItem
  }
}
